import _ from 'lodash'
import moment from 'moment'
import Department from './Department'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import User from '@/modules/user/model/User'
import Service from '@/modules/reservation/model/Service'
import Event from '@/modules/reservation/model/Event'
import Payment from '@/modules/payment/model/Payment';
import {ReservationHourRange} from '@/modules/reservation/model/ReservationHourRange';
import {ReservationDateRange} from '@/modules/reservation/api';

export enum ReservationPaymentMethod {
    Card = 'PAYMENT_METHOD_CARD',
    Cash = 'PAYMENT_METHOD_CASH',
    None = 'PAYMENT_METHOD_NONE',
}

/**
 * Reserva
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.e>
 */
export default class Reservation {
    public readonly tempId = _.uniqueId('reservation_')
    public id = 0
    public dateStart = new Date()
    public dateEnd = new Date()
    public department = new Department()
    public user = new User()
    public operator = new User()
    public services: Service[] = []
    public event = new Event()
    public clientUnigestId = ''
    public payment = new Payment()
    public price = 0
    public correlationId: number | string | null = null
    public paymentMethod: ReservationPaymentMethod | null = null
    private _documentId = ''
    public hourRange: ReservationHourRange | null = null
    public status: 'pending' | 'failed' | 'success' = 'pending'
    public error = ''
    public days = 0

    /**
     * Indica si la reserva acepta servicios
     */
    public allowServices = true

    public get documentId (): string {
        return this.payment.id ? this.payment.documentId : this._documentId
    }

    /**
     * Indica si la reserva es nueva y todavía no ha sido registrada en el
     * servidor
     */
    public get isNew (): boolean {
        return !this.id
    }

    /**
     * Precio de la reserva, basado en el precio del departamento mas el extra
     * por los servicios registrados
     */
    // public get price (): number {
    //     return this.department.price + this.servicesPrice
    // }

    /**
     * Sub-total del precio de la reserva solo incluyendo el importe respectivo a la suma
     * de los servicios contratados
     */
    public get servicesPrice (): number {
        return _.sumBy(this.services, (s) => s.price)
    }

    /**
     * Indica si la reserva tiene un tiempo de duración maximo
     */
    public hasDuration (): boolean {
        return !!this.department.duration
    }

    /**
     * Comprueba si la reserva se encuentra dentro de las horas argumentadas
     * @param start
     * @param end
     */
    public isBetweenHours (start: Date, end: Date) {
        start.setSeconds(0)
        end.setSeconds(0)
        this.dateEnd.setSeconds(0)
        this.dateStart.setSeconds(0)

        return start.getTime() >= this.dateStart.getTime() && end.getTime() <= this.dateEnd.getTime()

        // return moment(this.dateStart).isBetween(start, end) && moment(this.dateEnd).isBetween(start, end)
    }

    public static deserialize (data: KeyValuePair) {
        const reservation = new this()
        reservation.id = data.id
        reservation.dateStart = moment(data.date_start, 'DD-MM-YYYY HH:mm:ss').toDate()
        reservation.dateEnd = moment(data.date_end, 'DD-MM-YYYY HH:mm:ss').toDate()
        reservation.department = Department.deserialize(data.department)
        reservation.user = User.deserialize(data.user)
        reservation.operator = User.deserialize(data.operator)
        reservation.clientUnigestId = data.unigest_id
        reservation.services = (data.services || []).map((s: KeyValuePair) => Service.deserialize(s))
        reservation.correlationId = data.correlation_id || null
        reservation.paymentMethod = data.payment_method || ''
        reservation._documentId = data.document_id || ''
        reservation.price = data.price || 0
        if (data.event) {
            reservation.event = Event.deserialize(data.event)
        }

        if (data.payment) {
            reservation.payment = Payment.deserialize(data.payment)
        }

        return reservation;
    }

    public serialize (): KeyValuePair {
        return {
            user: this.user.id,
            department: this.department.id,
            date_start: moment(this.dateStart).format('DD-MM-YYYY HH:mm:ss'),
            date_end: moment(this.dateEnd).format('DD-MM-YYYY HH:mm:ss'),
            services: this.services.map(s => s.id),
            event: this.event.id,
            correlation_id: this.correlationId,
            payment_method: this.paymentMethod || ReservationPaymentMethod.None,
            days: this.days,
        }
    }
}
